import {QuoteService} from 'src/app/admin-core/apiservices/quote.service';
import {OverrideSrdPolicyModalComponent} from '../override-srd-policy-modal/override-srd-policy-modal.component';
import {DateHistoryModalComponent} from '../date-history-modal/date-history-modal.component';
import { ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AlertService, AuthService, DebugLoggerService, HelperService} from 'src/app/admin-core/services';
import {Constants} from "src/app/admin-core/constants";
import moment from 'moment';
import {Router} from "@angular/router";
import {forkJoin} from "rxjs";
import {FormAutofocusDirective} from '../../directives/form-autofocus.directive';

@Component({
  hostDirectives: [FormAutofocusDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
    selector: 'app-update-stock-required-date-modal',
    templateUrl: './update-stock-required-date-modal.component.html',
    styleUrls: ['./update-stock-required-date-modal.component.css', '../../../app.component.css']
})
export class UpdateStockRequiredDateModalComponent implements OnInit {
    @Input() modelOrder!: any;
    @Input() isFromQuoteProduct: boolean = false;
    @Input() is_offline_order: boolean = false;
    @Input() isFromQuoteEdit: boolean = false;
    @Input() showSfcdCheckbox: boolean = false;
    // Additional product IDs in the same delivery that should receive the same SRD update
    @Input() additionalQuoteProductIds: number[] = [];
    isLoading = false;
    deliveryDetailForm!: UntypedFormGroup;
    submitted = false;
    constants = Constants;
    stockRequiredDate: any
    @Input() fieldData!: any;
    @Input() dateType: string = 'SRD'; // 'SRD' | 'SCD'
    @Input() level: string = 'quote';  // 'quote' | 'product'
    minDate = new Date();
    displayWarehouseArrivalDate: any
    isInvalidDate: boolean = false;
    is_split_delivery: boolean;
    isSplitDeliveryDisabled: boolean = false;
    isdateChanged = false;
    availableStocksStatus = ["Available", "Low Stock", "In Stock", "Limited Sale Stock Only"];
    calculatedLeadTimeDate: Date | null = null;
    private previousSelectedDate: any = null;
    // GC-339 Phase 1: manual (admin-fixed) storage fee. Only offered at order level (order-level
    // amount), and only when the SFCD is already set & reached and the order isn't despatched.
    canToggleManualFee: boolean = false;
    manualFeeDisabledReason: string = '';

    constructor(
        public activeModel: NgbActiveModal,
        private fb: UntypedFormBuilder,
        public helperServices: HelperService,
        private alertService: AlertService,
        private router: Router,
        private quoteService: QuoteService,
        private debugLogger: DebugLoggerService,
        private authenticationService: AuthService,
        private ngbModal: NgbModal
    ) {
    }

    /** GC-339: open the manual storage-fee change history (activity type 100). Accessible regardless of the
     *  "Enter manual storage fees" checkbox state — mirrors the SFCD history clock button. */
    openManualStorageFeeHistoryModal(): void {
        const instance = this.ngbModal.open(DateHistoryModalComponent, {
            windowClass: 'date-history-modal',
            backdrop: 'static',
            centered: false,
        });
        instance.componentInstance.title = Constants.MSF_HISTORY_MODAL_TITLE;
        instance.componentInstance.type = Constants.MSF_HISTORY_TYPE;
        instance.componentInstance.level = 'quote';
        instance.componentInstance.entityId = this.modelOrder.id;
    }

    ngOnInit(): void {
        if (this.dateType === Constants.SCD_HISTORY_TYPE) {
            const originalScd = this.modelOrder.storage_commencement_date || null;

            // GC-339: decide whether the manual storage-fee toggle is available (order level only).
            this.computeManualFeeAvailability(originalScd);

            const manualEnabled = !!this.modelOrder.is_manual_storage_fee;
            this.deliveryDetailForm = this.fb.group({
                storage_commencement_date: new UntypedFormControl(null, [Validators.required]),
                note: new UntypedFormControl('', []),
                is_manual_storage_fee: new UntypedFormControl({
                    value: manualEnabled,
                    disabled: !this.canToggleManualFee
                }),
                manual_storage_fee_amount: new UntypedFormControl({
                    value: this.modelOrder.manual_storage_fee_amount ?? null,
                    disabled: !manualEnabled || !this.canToggleManualFee
                }, manualEnabled ? [Validators.required, Validators.min(0.01)] : [])
            });

            // Toggle the amount field's state/validators with the checkbox.
            this.deliveryDetailForm.get('is_manual_storage_fee')?.valueChanges.subscribe((checked: boolean) => {
                const amountControl = this.deliveryDetailForm.get('manual_storage_fee_amount');
                if (checked) {
                    amountControl?.enable({ emitEvent: false });
                    amountControl?.setValidators([Validators.required, Validators.min(0.01)]);
                } else {
                    amountControl?.clearValidators();
                    amountControl?.reset(null, { emitEvent: false });
                    amountControl?.disable({ emitEvent: false });
                }
                amountControl?.updateValueAndValidity({ emitEvent: false });
            });

            const formattedDate = originalScd ? new Date(originalScd) : null;
            this.deliveryDetailForm.patchValue({ storage_commencement_date: formattedDate });

            this.deliveryDetailForm.get('storage_commencement_date')?.valueChanges.subscribe((value) => {
                const noteControl = this.deliveryDetailForm.get('note');
                const originalDate = originalScd ? moment(originalScd).format('YYYY-MM-DD') : null;
                const selectedDate = value ? moment(value).format('YYYY-MM-DD') : null;
                this.isdateChanged = originalDate !== null && originalDate !== selectedDate;

                if (this.isdateChanged) {
                    noteControl?.setValidators([Validators.required]);
                } else {
                    noteControl?.clearValidators();
                    noteControl?.reset('');
                }
                noteControl?.updateValueAndValidity();
            });
            return;
        }

        this.deliveryDetailForm = this.fb.group({
            stock_required_date: new UntypedFormControl({
                value: null,
                disabled: this.modelOrder.is_split_delivery
            }, [Validators.required]),
            note: new UntypedFormControl('', []),
            update_sfcd: new UntypedFormControl(false)
        });

        this.isSplitDeliveryDisabled = (![this.constants.ORDER_RECEIVED_SHORT, this.constants.ORDER_DELAYED_IN_TRANSIT_SHORT, this.constants.ORDER_IN_PRODUCTION_SHORT, this.constants.ORDER_IN_TRANSIT_SHORT, this.constants.ORDER_CLEARED_CUSTOMS_SHORT, this.constants.ORDER_SPLIT_PRODUCTION_SHORT, this.constants.ORDER_PACKING_SHORT].includes(this.modelOrder.status?.short_name)) && this.modelOrder.type == this.constants.ORDER_TYPE_ORDER_SHORT;

        this.patchValueForDeliveryDetail()

        if (!this.isFromQuoteProduct && !this.isFromQuoteEdit) {
            this.getWarehouseArrivalDate();
            this.calculateMinimumSRD();
        }
        if (this.isFromQuoteProduct || this.isFromQuoteEdit) {
            this.calculateMinimumSRD();
        }

        this.deliveryDetailForm.get('stock_required_date')?.valueChanges.subscribe((value) => {
            if (this.isInvalidDate) {
                this.isInvalidDate = false;
                return;
            }
            if (value == Constants.INVALID_DATE_ERROR_MESSAGE) {
                this.alertService.error(Constants.VALID_DATE_ERROR_MESSAGE);
                if (this.modelOrder.stock_required_date) {
                    this.patchValueForDeliveryDetail()
                } else {
                    this.deliveryDetailForm.get('stock_required_date')?.reset();
                }
                return;
            }
            const noteControl = this.deliveryDetailForm.get('note');
            const originalDate = this.stockRequiredDate ? moment(this.stockRequiredDate).format('YYYY-MM-DD') : null;
            const selectedDate = value ? moment(value).format('YYYY-MM-DD') : null;
            this.isdateChanged = originalDate !== null && originalDate !== selectedDate;
            if (this.isdateChanged) {
                noteControl?.setValidators([Validators.required]);
            } else {
                noteControl?.clearValidators();
                noteControl?.reset('');
            }
            noteControl?.updateValueAndValidity();

            if (value && !this.is_split_delivery && this.isSelectedDateBeforeLeadTime(value)) {
                const prev = this.previousSelectedDate;
                const warningRef = this.ngbModal.open(OverrideSrdPolicyModalComponent, {
                    backdrop: 'static',
                    keyboard: false,
                    windowClass: 'donationModals quotes-model quote-order-export-pdf-model srd-override-policy-modal'
                });
                warningRef.result.then(
                    () => {
                        this.previousSelectedDate = value;
                    },
                    () => {
                        this.deliveryDetailForm.get('stock_required_date')?.setValue(prev, {emitEvent: false});
                    }
                );
            } else if (value) {
                this.previousSelectedDate = value;
            }
        });
    }

    /**
     * GC-339: manual storage fee is order-level and can only be edited when
     *  - this modal is at the quote (order) level,
     *  - the order isn't despatched/delivered (fees locked once despatched),
     *  - the SFCD is already set AND reached (<= today) — because unchecking reverts to auto,
     *    which anchors on the SFCD.
     */
    private computeManualFeeAvailability(originalScd: any): void {
        if (this.level !== 'quote') {
            this.canToggleManualFee = false;
            return;
        }

        const shortName = this.modelOrder?.status?.short_name;
        const isDespatched = shortName === Constants.ORDER_DELIVERY_DISPATCHED_SHORT
            || shortName === Constants.ORDER_DELIVERD_SHORT;
        if (isDespatched) {
            this.canToggleManualFee = false;
            this.manualFeeDisabledReason = 'Storage fees can no longer be edited once the order is despatched.';
            return;
        }

        if (!originalScd) {
            this.canToggleManualFee = false;
            this.manualFeeDisabledReason = 'Set the storage fees commencement date before entering a manual storage fee.';
            return;
        }
        const today = moment().startOf('day');
        if (moment(originalScd).startOf('day').isAfter(today)) {
            this.canToggleManualFee = false;
            this.manualFeeDisabledReason = 'Manual storage fees can be entered once the commencement date is reached.';
            return;
        }

        this.canToggleManualFee = true;
        this.manualFeeDisabledReason = '';
    }

    calculateSRD() {
        let productLeadTimeDates = [];
        this.modelOrder?.quote_product?.forEach(product => {
            let leadTimeWeeks = 0;
            const leadTime = product.product?.calculatedLeadTime;

            if (this.availableStocksStatus.includes(leadTime)) {
                leadTimeWeeks = 0;
            } else {
                const leadTimeArray = leadTime?.match(/\d+/g);
                const leadTimeWeeksArray = leadTimeArray?.map(Number);
                if (leadTimeWeeksArray && leadTimeWeeksArray.length > 0) {
                    leadTimeWeeks = Math.max(...leadTimeWeeksArray);
                }
            }
            let minDate = new Date();
            minDate.setDate(minDate.getDate() + (7 * leadTimeWeeks));
            productLeadTimeDates.push(minDate);
        });
        if (productLeadTimeDates.length > 0) {
            const stockRequiredStartDate = new Date(Math.max(...productLeadTimeDates.map((date: any) => date.getTime())));
            this.minDate.setTime(new Date(stockRequiredStartDate).getTime());
            return stockRequiredStartDate;
        }
    }

    calculateMinimumSRD(splitDeliveryState?: boolean) {
        let leadTimeWeeks = 0;
        // When called with an explicit splitDeliveryState (e.g. after the toggle in onChange),
        // use that value; otherwise fall back to what the model says.
        const isSplit = splitDeliveryState !== undefined ? splitDeliveryState : !!this.modelOrder?.isSplitDelivery;

        // Scenario 0: opened from the quote-edit screen against ONE line. That opener passes the
        // line's own calculatedLeadTime as modelOrder.leadTime, but it also passes the full product
        // list — and Scenario 1 below only consults modelOrder.leadTime when the list is ABSENT. So
        // the line's own figure was ignored and the MAXIMUM lead time across every line was used
        // instead.
        //
        // On a split that is precisely backwards. An In-Stock line was being judged against the
        // slowest line in the quote (14/10/2026 rather than today), so dating it for 30/09 — an
        // ordinary despatch for stock already on the floor — raised "You are about to override
        // company SRD policy". Splitting a delivery exists so that a fast line can go ahead of a
        // slow one; measuring the fast line against the slow one defeats it and trains people to
        // click through a warning that matters elsewhere.
        if (this.isFromQuoteEdit && this.modelOrder?.leadTime) {
            const lt = this.modelOrder.leadTime;
            if (!this.availableStocksStatus.includes(lt)) {
                const arr = lt.match(/\d+/g)?.map(Number);
                if (arr?.length) leadTimeWeeks = Math.min(...arr);
            }
        }
        // Scenario 1: Non-split — prefer per-product calculatedLeadTime (quantity-aware) over the
        // order-level leadTime, which is a static aggregate and won't reflect quantity changes.
        else if (!isSplit) {
            const products = this.modelOrder?.quote_product;
            if (products?.length) {
                // Take the most restrictive product: lowest bound of each product's range, then max across products.
                products.forEach((qp: any) => {
                    const lt = qp.product?.calculatedLeadTime;
                    if (lt && !this.availableStocksStatus.includes(lt)) {
                        const arr = lt.match(/\d+/g)?.map(Number);
                        if (arr?.length) leadTimeWeeks = Math.max(leadTimeWeeks, Math.min(...arr));
                    }
                });
            } else {
                // Fall back to order-level leadTime when product list is not available.
                const leadTimeSource = this.modelOrder?.leadTime;
                if (leadTimeSource && !this.availableStocksStatus.includes(leadTimeSource)) {
                    const arr = leadTimeSource.match(/\d+/g)?.map(Number);
                    if (arr?.length) leadTimeWeeks = Math.min(...arr);
                }
            }
        }
        // Scenario 2: Split delivery with a known delivery group — use only THIS product's own lead time
        else if (isSplit && this.modelOrder?.schedule_delivery_id) {
            const thisProduct = (this.modelOrder?.quote_product || []).find(
                (qp: any) => qp.id === this.modelOrder.quote_product_id
            );
            const lt = thisProduct?.product?.calculatedLeadTime;
            if (lt && !this.availableStocksStatus.includes(lt)) {
                const arr = lt.match(/\d+/g)?.map(Number);
                if (arr?.length) leadTimeWeeks = Math.min(...arr);
            }
        }
        // Scenario 3: Split delivery but product has no delivery group yet — use this product's own lead time
        else if (isSplit && !this.modelOrder?.schedule_delivery_id) {
            const thisProduct = (this.modelOrder?.quote_product || []).find(
                (qp: any) => qp.id === this.modelOrder.quote_product_id
            );
            const lt = thisProduct?.product?.calculatedLeadTime;
            if (lt && !this.availableStocksStatus.includes(lt)) {
                const arr = lt.match(/\d+/g)?.map(Number);
                if (arr?.length) leadTimeWeeks = Math.min(...arr);
            }
        }

        // Compute lead time threshold date (used for warning only; minDate stays as today)
        const leadTimeDate = new Date();
        leadTimeDate.setHours(0, 0, 0, 0);
        leadTimeDate.setDate(leadTimeDate.getDate() + (7 * leadTimeWeeks));
        this.calculatedLeadTimeDate = leadTimeDate;
    }

    getWarehouseArrivalDate() {
        const warehouseArrivalDate = this.modelOrder.quote_product?.[0]?.container?.anticipated_warehouse_arrival_date;
        const stockRequiredDate = this.modelOrder.stock_required_date;

        this.displayWarehouseArrivalDate = [];

        if (warehouseArrivalDate) {
            this.displayWarehouseArrivalDate.push({
                date: new Date(warehouseArrivalDate),
                classes: ['custom-warehouse-arrival-date-class']
            });
        }

        if (stockRequiredDate) {
            this.displayWarehouseArrivalDate.push({
                date: new Date(stockRequiredDate),
                classes: ['custom-selected-date-class']
            });
        }
    }

    get da() {
        return this.deliveryDetailForm.controls;
    }

    patchValueForDeliveryDetail() {
        this.stockRequiredDate = this.modelOrder.stock_required_date; // keep as raw value
        this.is_split_delivery = this.modelOrder.is_split_delivery;

        const formattedDate = this.modelOrder.stock_required_date ? new Date(this.modelOrder.stock_required_date) : null;
        this.previousSelectedDate = formattedDate;

        this.deliveryDetailForm?.patchValue({
            stock_required_date: formattedDate
        });
    }

    cancel() {
        this.activeModel.close(false);
    }

    onSubmitScd() {
        this.submitted = true;
        this.deliveryDetailForm.markAllAsTouched();

        if (this.deliveryDetailForm.invalid) return;
        this.isLoading = true;

        const selectedDate = this.deliveryDetailForm.value.storage_commencement_date;
        const formattedDate = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null;

        const request$ = this.level === 'product'
            ? this.submitSplitDeliveryUpdate({
                storage_commencement_date: formattedDate,
                previous_storage_commencement_date: this.modelOrder.storage_commencement_date || null
            })
            : (() => {
                const model = {
                    id: this.modelOrder.id,
                    storage_commencement_date: formattedDate,
                    note: this.deliveryDetailForm.value.note,
                    previous_storage_commencement_date: this.modelOrder.storage_commencement_date || null
                };
                return this.quoteService.updateStorageCommencementDate(model);
            })();

        request$.subscribe({
            next: async () => {
                try {
                    // GC-339: manual storage fee is a SEPARATE order-level call, fired after the SFCD save
                    // (which may have just set/reached the SFCD the enable path requires). Quote level only.
                    if (this.level === 'quote' && this.hasManualFeeChanged()) {
                        await this.quoteService.updateManualStorageFee(this.buildManualFeePayload()).toPromise();
                    }
                    this.isLoading = false;
                    this.alertService.success(Constants.STORAGE_COMMENCEMENT_DATE_UPDATE);
                    this.activeModel.close(formattedDate);
                } catch (error: any) {
                    this.alertService.error(error);
                    this.isLoading = false;
                }
            },
            error: (error: any) => {
                this.alertService.error(error);
                this.isLoading = false;
            }
        });
    }

    /** GC-339: has the manual storage-fee toggle or amount changed vs the persisted order? */
    private hasManualFeeChanged(): boolean {
        if (!this.canToggleManualFee) return false;
        const raw = this.deliveryDetailForm.getRawValue();
        const newManual = !!raw.is_manual_storage_fee;
        const oldManual = !!this.modelOrder.is_manual_storage_fee;
        if (newManual !== oldManual) return true;
        if (!newManual) return false;
        const newAmount = Number(raw.manual_storage_fee_amount);
        const oldAmount = this.modelOrder.manual_storage_fee_amount != null
            ? Number(this.modelOrder.manual_storage_fee_amount)
            : null;
        return newAmount !== oldAmount;
    }

    /** GC-339: payload for PATCH quote/update-manual-storage-fee/:id */
    private buildManualFeePayload(): any {
        const raw = this.deliveryDetailForm.getRawValue();
        const enable = !!raw.is_manual_storage_fee;
        return {
            id: this.modelOrder.id,
            is_manual_storage_fee: enable,
            manual_storage_fee_amount: enable ? Number(raw.manual_storage_fee_amount) : null,
            note: raw.note || null
        };
    }

    onSubmit() {
        this.submitted = true;
        this.deliveryDetailForm.markAllAsTouched();

        if (this.deliveryDetailForm.invalid) {
            return;
        }

        if (this.is_split_delivery && !this.modelOrder?.quote_address_id && !this.isFromQuoteProduct && !this.isFromQuoteEdit) {
            this.alertService.error('Add delivery address.');
            return;
        }

        this.isLoading = true;

        if (this.isFromQuoteEdit) {
            this.isLoading = false;
            this.activeModel.close({
                stockRequiredDate: this.deliveryDetailForm.value.stock_required_date,
                note: this.deliveryDetailForm.value.note
            });
            return;
        }

        let json: any = {
            id: this.modelOrder.id,
            note: this.deliveryDetailForm.value.note,
            previous_stock_required_date: this.modelOrder.stock_required_date || null,
            backend_user_id: this.authenticationService.currentUserValue?.id
        };
        if (this.is_split_delivery != this.modelOrder.is_split_delivery) {
            json.is_split_delivery = this.is_split_delivery;
        }

        const selectedDate = this.deliveryDetailForm.controls.stock_required_date.value;

        if (selectedDate) {
            const formattedSelectedDate = moment(selectedDate).format('YYYY-MM-DD');
            json.stock_required_date = formattedSelectedDate;
        }

        // this.quoteService.updateStockRequiredDate(json).subscribe(
        //     async () => {
        //         if (this.is_split_delivery != this.modelOrder.is_split_delivery) {
        //             this.quoteService.splitDelivery(this.modelOrder.id).subscribe(
        //                 () => {
        //                     this.isLoading = false;
        //                     this.alertService.success(Constants.STOCK_REQUIRED_DATE_UPDATE);
        //                     this.activeModel.close(true);
        //                 },
        //                 (error) => {
        //                     this.alertService.error(error);
        //                     this.isLoading = false;
        //                 }
        //             );
        //             if (this.modelOrder.type === Constants.ORDER_TYPE_ORDER_SHORT && !this.is_offline_order) {
        //                 await this.syncAdditionalDeliveryDetailWithUnleashed();
        //             }
        //         } else {
        //             this.isLoading = false;
        //             this.alertService.success(Constants.STOCK_REQUIRED_DATE_UPDATE);
        //             this.activeModel.close(true);
        //         }
        //     },
        //     () => {
        //         this.isLoading = false;
        //     }
        // );

        // Use a single pipeline to handle the sequence
        this.quoteService.updateStockRequiredDate(json).subscribe({
            next: async () => {
                try {
                    const isSplitChanged = this.is_split_delivery != this.modelOrder.is_split_delivery;

                    if (isSplitChanged) {
                        // 1. Wait for Split Delivery to finish
                        // We convert the observable to a promise to ensure the 'await' works correctly
                        await this.quoteService.splitDelivery(this.modelOrder.id).toPromise();

                        // 2. If it's an Order, wait for Unleashed Sync
                        if (this.modelOrder.type === Constants.ORDER_TYPE_ORDER_SHORT && !this.is_offline_order) {
                            await this.syncAdditionalDeliveryDetailWithUnleashed();
                        }
                    }

                    if (this.deliveryDetailForm.value.update_sfcd && json.stock_required_date && !this.is_split_delivery) {
                        const scdModel = {
                            id: this.modelOrder.id,
                            storage_commencement_date: json.stock_required_date,
                            note: this.deliveryDetailForm.value.note,
                            previous_storage_commencement_date: this.modelOrder.storage_commencement_date || null
                        };
                        await this.quoteService.updateStorageCommencementDate(scdModel).toPromise();
                    }

                    // ✅ Everything is done (Date updated -> Split done -> Sync done)
                    this.isLoading = false;
                    this.alertService.success(Constants.STOCK_REQUIRED_DATE_UPDATE);
                    this.activeModel.close(true);
                } catch (error) {
                    // Handles errors from splitDelivery or sync
                    this.alertService.error(error);
                    this.isLoading = false;
                }
            },
            error: (error) => {
                // Handles errors from updateStockRequiredDate
                this.alertService.error(error);
                this.isLoading = false;
            }
        });
    }

    syncAdditionalDeliveryDetailWithUnleashed() {
        if (this.modelOrder.id > 0) {
            return new Promise((resolve, reject) => {
                this.quoteService.syncAdditionalDeliveryWithUnleashed(null, this.modelOrder.id).subscribe(() => {
                    resolve(true);
                }, () => {
                    this.isLoading = false;
                    reject(false);
                });
            });
        }
    }

    // Optional formatting function for display (not used in patching)
    formatDate(date: string | Date) {
        return date ? moment(date).format('DD/MM/YYYY') : '';
    }

    onClickToRedirectCalendarView() {
        this.activeModel.close(false);
        localStorage.setItem('open_calendar_order_id', this.modelOrder?.id);
        this.router.navigate([`despatch/calendar/`], {});
    }

    onChange(event: any) {
        if (this.isSplitDeliveryDisabled) {
            return;
        }
        if (event?.target?.checked) {
            this.is_split_delivery = Constants.IS_TRUE;
        } else {
            this.is_split_delivery = Constants.IS_FALSE;
            // Recalculate the lead time threshold using normal-delivery logic so that
            // the override warning fires correctly if the current SRD is before the
            // order-level lead time (Scenario 1).
            this.calculateMinimumSRD(false);
        }
        if (this.modelOrder.is_split_delivery != this.is_split_delivery)
            this.deliveryDetailForm.get('stock_required_date')?.enable({ emitEvent: false });
        else if (this.modelOrder.is_split_delivery) {
            const formattedDate = this.modelOrder.stock_required_date ? new Date(this.modelOrder.stock_required_date) : null;
            this.deliveryDetailForm?.patchValue({
                stock_required_date: formattedDate
            }, { emitEvent: false });
            this.deliveryDetailForm.get('stock_required_date')?.disable({ emitEvent: false });
        }
        this.deliveryDetailForm.get('stock_required_date')?.updateValueAndValidity({ emitEvent: false });
    }

    private submitSplitDeliveryUpdate(datePayload: {
        stock_required_date?: string;
        previous_stock_required_date?: string | null;
        storage_commencement_date?: string | null;
        previous_storage_commencement_date?: string | null;
    }, quoteProductId?: number) {
        const json = {
            quote_product_id: quoteProductId ?? this.modelOrder.quote_product_id,
            schedule_delivery_id: this.modelOrder.schedule_delivery_id,
            note: this.deliveryDetailForm.value.note,
            backend_user_id: this.authenticationService.currentUserValue?.id,
            ...datePayload
        };
        return this.quoteService.updateSplitDelivery(this.modelOrder.id, json);
    }

    private isSelectedDateBeforeLeadTime(selectedDate: Date): boolean {
        if (!this.calculatedLeadTimeDate || !selectedDate) return false;
        const selected = moment(selectedDate).startOf('day');
        const threshold = moment(this.calculatedLeadTimeDate).startOf('day');
        return selected.isBefore(threshold);
    }

    updateStockRequiredDate() {
        this.submitted = true;
        this.deliveryDetailForm.markAllAsTouched();
        if (this.deliveryDetailForm.invalid) return;

        this.isLoading = true;

        const formattedDate = moment(this.deliveryDetailForm.value.stock_required_date).format('YYYY-MM-DD');
        const datePayload:any = {
            stock_required_date: formattedDate,
            previous_stock_required_date: this.modelOrder.stock_required_date || null
        };
        if (this.deliveryDetailForm.value.update_sfcd && !this.is_split_delivery) {
            datePayload.storage_commencement_date = formattedDate;
            datePayload.previous_storage_commencement_date = this.modelOrder.storage_commencement_date || null;
        }

        // Build one API call per product in the delivery (primary + any additional)
        const calls = [
            this.submitSplitDeliveryUpdate(datePayload),
            ...this.additionalQuoteProductIds.map(id => this.submitSplitDeliveryUpdate(datePayload, id))
        ];

        forkJoin(calls).subscribe({
            next: async () => {
                this.debugLogger.log(this.is_offline_order);
                if (this.modelOrder.type === Constants.ORDER_TYPE_ORDER_SHORT && !this.is_offline_order) {
                    await this.syncAdditionalDeliveryDetailWithUnleashed();
                }
                this.alertService.success(Constants.STOCK_REQUIRED_DATE_UPDATE);
                this.activeModel.close(formattedDate);
                this.isLoading = false;
            },
            error: (error: any) => {
                this.alertService.error(error);
                this.isLoading = false;
            }
        });
    }
}
